import { validateFormSchema } from "@domain/form";
import { useParams } from "@tanstack/react-router";
import { Alert, Card, Col, Empty, Row, Space, Spin, Tag, Typography } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { FieldListPanel } from "../components/FieldListPanel";
import { FieldSettingsPanel } from "../components/FieldSettingsPanel";
import { FormPreview } from "../components/FormPreview";
import { PublishPanel } from "../components/PublishPanel";
import {
	useLeadFormDetail,
	useLeadFormVersion,
	usePublishLeadFormVersion,
} from "../hooks/useLeadForms";
import type { FormField, FormFieldType } from "../types";
import { createField } from "../types";

const { Title } = Typography;

export function LeadFormBuilderPage() {
	/**
	 * ⚠️ `strict: false` — sahifa `routeTree.tsx` ni IMPORT QILMAYDI
	 * (poydevor fayli, F1 unga tegmaydi). Bo'shashgan variant marshrut
	 * obyektiga bog'lanmasdan parametrni o'qiydi.
	 *
	 * ⚠️ Bu loyihada BIRINCHI parametrli marshrut — bugungi
	 * `routeTree.tsx` da `$` li yo'l umuman yo'q, ya'ni `useParams` naqshi
	 * hali sinalmagan. Agar poydevor `getRouteApi("/lead-forms/$formId")`
	 * naqshini tanlagan bo'lsa, u ham qabul qilinadi (ikkalasi ham
	 * `routeTree.tsx` ni import qilmaydi) — muhimi, `formId` ni sahifa
	 * o'zi o'qishi va `routeTree.tsx` ga TEGMASLIK.
	 */
	const { t } = useTranslation("leadForms");
	const params = useParams({ strict: false }) as { formId?: string };
	const formId = params.formId ?? null;

	const detail = useLeadFormDetail(formId);
	const latest = detail.data?.latestVersion ?? null;
	const version = useLeadFormVersion(formId, latest);
	const publish = usePublishLeadFormVersion(formId);

	const [fields, setFields] = useState<FormField[]>([]);
	const [selectedKey, setSelectedKey] = useState<string | null>(null);

	/**
	 * ⚠️ Qoralama BRAUZERDA yashaydi (spec §7.2): boshlang'ich holat eng
	 * oxirgi nashr etilgan versiyadan olinadi, keyingi tahrirlar esa
	 * faqat state'da. Serverga yozadigan yagona amal — «Nashr».
	 */
	useEffect(() => {
		if (version.data) {
			setFields(version.data.schema.fields);
			setSelectedKey(version.data.schema.fields[0]?.key ?? null);
		}
	}, [version.data]);

	// Maydon yonidagi belgi uchun (spec §7.3). Manba — domain'ning
	// `validateFormSchema` i, ya'ni server bilan bir xil qoidalar.
	//
	// ⚠️ Erta `return` lardan OLDIN turadi — Hook'lar shart-sharoitsiz,
	// har renderda bir xil tartibda chaqirilishi kerak (Hooks qoidasi).
	const schemaErrors = useMemo(() => {
		const map = new Map<string, string[]>();

		for (const error of validateFormSchema({ fields })) {
			if (!error.fieldKey) {
				continue;
			}

			map.set(error.fieldKey, [...(map.get(error.fieldKey) ?? []), error.message]);
		}

		return map;
	}, [fields]);

	if (!formId) {
		return <Empty description={t("builder.formNotSelected")} />;
	}

	if (detail.isLoading || version.isLoading) {
		return <Spin />;
	}

	const selected = fields.find((field) => field.key === selectedKey) ?? null;

	const handleAdd = (type: FormFieldType) => {
		const field = createField(
			type,
			fields.map((item) => item.key)
		);

		setFields((current) => [...current, field]);
		setSelectedKey(field.key);
	};

	const handleMove = (key: string, delta: -1 | 1) => {
		setFields((current) => {
			const index = current.findIndex((field) => field.key === key);
			const target = index + delta;

			if (index < 0 || target < 0 || target >= current.length) {
				return current;
			}

			const next = [...current];
			const [moved] = next.splice(index, 1);

			if (moved) {
				next.splice(target, 0, moved);
			}

			return next;
		});
	};

	const handleFieldChange = (next: FormField) => {
		setFields((current) => current.map((field) => (field.key === selectedKey ? next : field)));
		setSelectedKey(next.key);
	};

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<Space>
					<Title level={3} className="!mb-0">
						{detail.data?.name ?? t("builder.defaultTitle")}
					</Title>
					{latest === null ? (
						<Tag>{t("builder.notPublished")}</Tag>
					) : (
						<Tag color="blue">{t("builder.currentVersion", { version: latest })}</Tag>
					)}
				</Space>
				<PermissionGate resource="lead-forms" action="update">
					<PublishPanel
						fields={fields}
						disabled={detail.data?.isActive === false}
						isPublishing={publish.isPending}
						onPublish={(schema) => publish.mutate(schema)}
					/>
				</PermissionGate>
			</div>

			{detail.data?.isActive === false && (
				<Alert
					type="warning"
					showIcon
					message={t("builder.archivedTitle")}
					description={t("builder.archivedDescription")}
				/>
			)}

			<Row gutter={16}>
				<Col span={8}>
					<Card title={t("builder.fieldsCard")} size="small">
						<FieldListPanel
							fields={fields}
							selectedKey={selectedKey}
							errors={schemaErrors}
							onSelect={setSelectedKey}
							onAdd={handleAdd}
							onRemove={(key) => {
								setFields((current) => current.filter((field) => field.key !== key));
								setSelectedKey(null);
							}}
							onMove={handleMove}
						/>
					</Card>
				</Col>
				<Col span={10}>
					<Card title={t("builder.settingsCard")} size="small">
						{selected ? (
							<FieldSettingsPanel
								field={selected}
								allFields={fields}
								onChange={handleFieldChange}
							/>
						) : (
							<Empty description={t("builder.selectField")} />
						)}
					</Card>
				</Col>
				<Col span={6}>
					<FormPreview fields={fields} />
				</Col>
			</Row>
		</div>
	);
}
